import { RefObject, useCallback, useRef } from 'react';
import Image from 'next/image';
import NextLink from 'next/link';

import { Box, Button, Flex, Heading, Icon, LinkBox, LinkOverlay, Text } from '@chakra-ui/react';

import {
  CircleArrowRightIcon,
  PlayIcon,
  RocketIcon,
  SchoolIcon,
  TicketIcon,
} from '~/features/experiments/ab_plp_redesign_2025/icons';
import { EndsSoonEyebrow } from '~/features/promotions/components/ends-soon-eyebrow';
import { FlashSaleEyebrow } from '~/features/promotions/components/flash-sale-eyebrow';
import { type July4848PromoVariant, JULY_48_48_PROMO_VARIANTS } from '~/features/promotions/constants/july-48-48-promo';
import {
  type Summer2026SaleVariant,
  SUMMER_2026_SALE_VARIANTS,
} from '~/features/promotions/constants/summer-2026-sale';

import {
  ATF_PROMO_ASSET_INTRINSIC_PX,
  ATF_PROMO_ASSETS,
  EXPERIENCE_ATF_CARD_SHELL_HEIGHT_PX,
  EXPERIENCE_ATF_CARD_SHELL_HEIGHT_PX_MOBILE,
  EXPERIENCE_ATF_CARD_SHELL_WIDTH_PX,
  EXPERIENCE_ATF_CARD_SHELL_WIDTH_PX_MOBILE,
  EXPERIENCE_ATF_CAROUSEL_MIN_HEIGHT_PX,
  EXPERIENCE_ATF_CAROUSEL_MIN_HEIGHT_PX_MOBILE,
  EXPERIENCE_ATF_SLIDE_WIDTH_PX,
  EXPERIENCE_ATF_SLIDE_WIDTH_PX_MOBILE,
  ExperienceAtfCardConfig,
  ExperienceAtfPromoVariant,
} from '../constants';

import { useCardViewTracking } from './use-card-view-tracking';

/** Scales Figma desktop decoration metrics to the narrower mobile shell (260 vs 300). */
const ATF_CARD_SHELL_MOBILE_SCALE = EXPERIENCE_ATF_CARD_SHELL_WIDTH_PX_MOBILE / EXPERIENCE_ATF_CARD_SHELL_WIDTH_PX;

function atfCardScaledMobilePx(desktopPx: number): string {
  return `${Math.round(desktopPx * ATF_CARD_SHELL_MOBILE_SCALE)}px`;
}

/**
 * Next/Image `sizes` for full-card `fill` decorations: slot width matches promo shell (mobile vs xl+).
 * Align media query with Chakra `xl` (~1280px / 80em) so srcset picks ~1x–2x assets correctly.
 */
const ATF_PROMO_DECORATION_SIZES = '(max-width: 1279px) 260px, 300px';

/** Schools grid max rendered width (matches `maxWidth` on the wrapper). */
const ATF_SCHOOLS_GRID_SIZES = '(max-width: 1279px) 178px, 205px';

const experienceAtfCardOuterLayout = {
  height: {
    base: `${EXPERIENCE_ATF_CAROUSEL_MIN_HEIGHT_PX_MOBILE}px`,
    xl: `${EXPERIENCE_ATF_CAROUSEL_MIN_HEIGHT_PX}px`,
  },
  width: {
    base: `${EXPERIENCE_ATF_SLIDE_WIDTH_PX_MOBILE}px`,
    xl: `${EXPERIENCE_ATF_SLIDE_WIDTH_PX}px`,
  },
} as const;

const experienceAtfCardShellLayout = {
  height: {
    base: `${EXPERIENCE_ATF_CARD_SHELL_HEIGHT_PX_MOBILE}px`,
    xl: `${EXPERIENCE_ATF_CARD_SHELL_HEIGHT_PX}px`,
  },
  padding: { base: '16px', xl: '24px' },
  width: {
    base: `${EXPERIENCE_ATF_CARD_SHELL_WIDTH_PX_MOBILE}px`,
    xl: `${EXPERIENCE_ATF_CARD_SHELL_WIDTH_PX}px`,
  },
} as const;

/** One step smaller than UDS `size="icon-left"` (font, height, horizontal padding). */
const experienceAtfCardCtaCompactSx = {
  fontSize: 'sm',
  h: '36px',
  lineHeight: 'sm',
  minH: '36px',
  px: '10px',
} as const;

const experienceAtfCardCtaIconSize = '12px';

/** MBA gold outline — same metrics as `experienceAtfCardCtaCompactSx`, Figma stroke 1.33px. */
const experienceAtfMbaCtaSx = {
  ...experienceAtfCardCtaCompactSx,
  alignItems: 'center',
  bg: 'transparent',
  border: '2px solid',
  borderColor: 'gold.100',
  borderRadius: '4px',
  color: 'gold.100',
  display: 'inline-flex',
  fontWeight: '400',
  justifyContent: 'center',
} as const;

/** Marty Cagan white outline — Figma 7665:32868, 1.33px stroke. */
const experienceAtfMartyCaganCtaSx = {
  ...experienceAtfCardCtaCompactSx,
  alignItems: 'center',
  bg: 'transparent',
  border: '1.33px solid',
  borderColor: 'white',
  borderRadius: '4px',
  color: 'white',
  display: 'inline-flex',
  fontWeight: '400',
  justifyContent: 'center',
} as const;

/** Summer sale black outline CTA — Figma 1035:81812, 1.33px stroke. */
const experienceAtfSummerSaleCtaSx = {
  ...experienceAtfCardCtaCompactSx,
  alignItems: 'center',
  bg: 'transparent',
  border: '1.33px solid',
  borderColor: 'black',
  borderRadius: '4px',
  color: 'black',
  display: 'inline-flex',
  fontSize: '12px',
  fontWeight: '400',
  justifyContent: 'center',
  lineHeight: 1.4,
  paddingLeft: '12px',
  paddingRight: '16px',
  paddingY: '10px',
} as const;

/** 48/48 July promo white outline CTA — Figma 1672:3950, 1.33px stroke. */
const experienceAtfJuly4848CtaSx = {
  ...experienceAtfCardCtaCompactSx,
  alignItems: 'center',
  bg: 'transparent',
  border: '1.33px solid',
  borderColor: 'white',
  borderRadius: '4px',
  color: 'white',
  display: 'inline-flex',
  fontWeight: '400',
  justifyContent: 'center',
  paddingLeft: '12px',
  paddingRight: '16px',
  paddingY: '10px',
} as const;

/** Decorative CTA chrome (real activation is the full-card link or native button parent). */
const experienceAtfCardCtaButtonProps = {
  as: 'span' as const,
  colorScheme: 'white',
  pointerEvents: 'none' as const,
  size: 'icon-left' as const,
  sx: experienceAtfCardCtaCompactSx,
  tabIndex: -1,
  variant: 'outline' as const,
};

function CardCtaChrome({ card }: { card: ExperienceAtfCardConfig }) {
  switch (card.promoVariant) {
    case 'july_4th_sale':
      return (
        <Button
          {...experienceAtfCardCtaButtonProps}
          aria-hidden={true}
          leftIcon={<RocketIcon boxSize={experienceAtfCardCtaIconSize} />}
        >
          {card.ctaLabel}
        </Button>
      );
    case 'july_48_48_g1':
    case 'july_48_48_g2':
      return (
        <Button
          aria-hidden={true}
          as='span'
          leftIcon={<CircleArrowRightIcon boxSize={experienceAtfCardCtaIconSize} color='white' />}
          pointerEvents='none'
          size='icon-left'
          sx={experienceAtfJuly4848CtaSx}
          tabIndex={-1}
          variant='unstyled'
        >
          {card.ctaLabel}
        </Button>
      );
    case 'summer_sale':
      return (
        <Button
          aria-hidden={true}
          as='span'
          leftIcon={<RocketIcon boxSize={experienceAtfCardCtaIconSize} />}
          pointerEvents='none'
          size='icon-left'
          sx={experienceAtfSummerSaleCtaSx}
          tabIndex={-1}
          variant='unstyled'
        >
          {card.ctaLabel}
        </Button>
      );
    case 'agentic_ai':
      return (
        <Button
          {...experienceAtfCardCtaButtonProps}
          aria-hidden={true}
          leftIcon={<RocketIcon boxSize={experienceAtfCardCtaIconSize} />}
        >
          {card.ctaLabel}
        </Button>
      );
    case 'free':
      return (
        <Button {...experienceAtfCardCtaButtonProps} aria-hidden={true} leftIcon={<FreeProgramsCtaIcon />}>
          {card.ctaLabel}
        </Button>
      );
    case 'mba':
      return (
        <Button
          aria-hidden={true}
          as='span'
          leftIcon={<SchoolIcon boxSize={experienceAtfCardCtaIconSize} />}
          pointerEvents='none'
          size='icon-left'
          sx={experienceAtfMbaCtaSx}
          tabIndex={-1}
          variant='unstyled'
        >
          {card.ctaLabel}
        </Button>
      );
    case 'marty_cagan_webinar':
      return (
        <Button
          aria-hidden={true}
          as='span'
          leftIcon={<PlayIcon boxSize={experienceAtfCardCtaIconSize} />}
          pointerEvents='none'
          size='icon-left'
          sx={experienceAtfMartyCaganCtaSx}
          tabIndex={-1}
          variant='unstyled'
        >
          {card.ctaLabel}
        </Button>
      );
    case 'build_ai_agents_webinar':
      return (
        <Button
          aria-hidden={true}
          as='span'
          leftIcon={<TicketIcon boxSize={experienceAtfCardCtaIconSize} />}
          pointerEvents='none'
          size='icon-left'
          sx={experienceAtfMartyCaganCtaSx}
          tabIndex={-1}
          variant='unstyled'
        >
          {card.ctaLabel}
        </Button>
      );
    default:
      return (
        <Button {...experienceAtfCardCtaButtonProps} aria-hidden={true}>
          {card.ctaLabel}
        </Button>
      );
  }
}

/** Figma node 7386:13902 — free-program CTA icon */
function FreeProgramsCtaIcon() {
  return (
    <Icon boxSize={experienceAtfCardCtaIconSize} viewBox='0 0 14 14' aria-hidden>
      <path
        d='M10.5 1.75H4.375C3.72967 1.75 3.20833 2.27133 3.20833 2.91667V11.0833C3.20833 11.7287 3.72967 12.25 4.375 12.25H10.5C11.1453 12.25 11.6667 11.7287 11.6667 11.0833V2.91667C11.6667 2.27133 11.1453 1.75 10.5 1.75ZM10.5 11.0833H4.375V2.91667H10.5V11.0833Z'
        fill='currentColor'
      />
      <path
        d='M5.25 4.08325H9.625V5.24992H5.25V4.08325ZM5.25 6.41659H9.625V7.58325H5.25V6.41659Z'
        fill='currentColor'
      />
    </Icon>
  );
}

export function ExperienceAtfHeroCard({
  card,
  dedupeViewIdsRef,
  position,
  onClick,
  onVideo,
}: {
  card: ExperienceAtfCardConfig;
  dedupeViewIdsRef: RefObject<Set<string>>;
  position: number;
  onClick: (card: ExperienceAtfCardConfig) => void;
  onVideo: (card: ExperienceAtfCardConfig) => void;
}) {
  const cardRef = useRef<HTMLDivElement>(null);

  useCardViewTracking(
    cardRef,
    { cardId: card.id, cardPosition: position, promoVariant: card.promoVariant },
    dedupeViewIdsRef
  );

  const palette = promoPalette(card.promoVariant);
  const isAgenticCard = card.promoVariant === 'agentic_ai';
  const isSummerSaleCard = card.promoVariant === 'summer_sale';
  const isMartyCaganCard = card.promoVariant === 'marty_cagan_webinar';
  const isBuildAiAgentsWebinarCard = card.promoVariant === 'build_ai_agents_webinar';
  const isWebinarPortraitCard = isMartyCaganCard || isBuildAiAgentsWebinarCard;

  const cardLinkAriaLabel = `${card.title}${card.titleLine2 ? ` ${card.titleLine2}` : ''}: ${card.ctaLabel}`;

  const cardBody = (
    <>
      <PromoDecoration imagePriority={position === 1} promoVariant={card.promoVariant} />

      <Flex
        flexDirection='column'
        flexGrow={1}
        gap={isWebinarPortraitCard ? { base: '10px', xl: '12px' } : { base: '16px', xl: '24px' }}
        justifyContent='space-between'
        minHeight={0}
        pointerEvents='none'
        position='relative'
        zIndex={isWebinarPortraitCard ? 3 : 2}
      >
        <Box>
          {card.promoVariant === 'july_4th_sale' ? (
            <Box marginBottom={{ base: 3, xl: 4 }} width='100%'>
              <EndsSoonEyebrow size='compact' variant='july_4th' />
            </Box>
          ) : card.promoVariant === 'summer_sale' ? (
            <Box marginBottom={{ base: 3, xl: 4 }} width='100%'>
              <EndsSoonEyebrow size='compact' variant='summer' />
            </Box>
          ) : card.promoVariant === 'july_48_48_g1' || card.promoVariant === 'july_48_48_g2' ? (
            <Box marginBottom={{ base: 3, xl: 4 }} width='100%'>
              <FlashSaleEyebrow size='compact' />
            </Box>
          ) : (
            <Flex alignItems='center' gap={2} marginBottom={{ base: 3, xl: 4 }} width='100%'>
              <Text
                color={palette.eyebrowColor}
                fontFamily='condensed'
                fontSize='13px'
                fontWeight='500'
                letterSpacing='0.1em'
                lineHeight={1}
                textTransform='uppercase'
                whiteSpace='nowrap'
              >
                {card.eyebrow}
              </Text>
              <Box borderBottom='1px solid' borderColor={palette.ruleColor} flex={1} minWidth={0} opacity={0.6} />
            </Flex>
          )}

          <Heading
            as='h2'
            color={palette.titleColor}
            fontSize={{ base: '1.375rem', xl: '1.5rem' }}
            fontWeight='600'
            letterSpacing='-0.015em'
            lineHeight={{ base: 1.2, xl: 1.15 }}
            marginBottom={2}
          >
            {card.title}
            {card.titleLine2 ? (
              <>
                <br />
                {card.titleLine2}
              </>
            ) : null}
          </Heading>

          {card.description ? (
            <Text
              color={palette.bodyColor}
              fontSize={{ base: '12px', xl: '13px' }}
              lineHeight={1.4}
              maxWidth={isSummerSaleCard ? '185px' : undefined}
            >
              {card.description}
            </Text>
          ) : null}
        </Box>

        {isMartyCaganCard ? (
          <MartyCaganPortrait imagePriority={position === 1} />
        ) : isBuildAiAgentsWebinarCard ? (
          <BuildAiAgentsWebinarPortraitAndDateRow imagePriority={position === 1} />
        ) : (
          <Flex flex={isAgenticCard ? 0 : 1} flexDirection='column' justifyContent='flex-end' minHeight={0}>
            {card.promoVariant === 'free' ? <FreeProgramsSchoolsGrid imagePriority={position === 1} /> : null}
          </Flex>
        )}

        <Box alignSelf='flex-start' marginTop='auto'>
          <CardCtaChrome card={card} />
        </Box>
      </Flex>
    </>
  );

  const sharedCardShellProps = {
    bgGradient: palette.cardGradient,
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column' as const,
    height: experienceAtfCardShellLayout.height,
    isolation: 'isolate' as const,
    overflow: 'hidden',
    padding: experienceAtfCardShellLayout.padding,
    position: 'relative' as const,
    width: experienceAtfCardShellLayout.width,
  };

  const handleVideoCardActivate = useCallback(() => {
    onVideo(card);
  }, [card, onVideo]);

  return (
    <Box
      ref={cardRef}
      alignItems='center'
      display='flex'
      height={experienceAtfCardOuterLayout.height}
      justifyContent='center'
      width={experienceAtfCardOuterLayout.width}
    >
      {card.action.type === 'video' ? (
        <Box
          _focusVisible={{ boxShadow: '0 0 0 3px rgba(23, 92, 255, 0.45)' }}
          appearance='none'
          aria-label={cardLinkAriaLabel}
          as='button'
          bg='transparent'
          border='none'
          borderRadius='8px'
          cursor='pointer'
          margin={0}
          padding={0}
          textAlign='inherit'
          type='button'
          onClick={handleVideoCardActivate}
        >
          <Box {...sharedCardShellProps}>{cardBody}</Box>
        </Box>
      ) : (
        <LinkBox {...sharedCardShellProps}>
          {cardBody}
          <LinkOverlay
            aria-label={cardLinkAriaLabel}
            as={NextLink}
            display='block'
            height='100%'
            href={card.action.href}
            left={0}
            position='absolute'
            top={0}
            width='100%'
            zIndex={3}
            onClick={() => onClick(card)}
          />
        </LinkBox>
      )}
    </Box>
  );
}

function promoPalette(variant: ExperienceAtfPromoVariant) {
  switch (variant) {
    case 'free':
      return {
        bodyColor: 'gray.100',
        cardGradient: 'linear(to-b, #0A0B0F, #25262A)',
        eyebrowColor: 'gray.300',
        ruleColor: 'gray.500',
        titleColor: 'white',
      };
    case 'agentic_ai':
      return {
        bodyColor: '#FFDCC3',
        cardGradient: 'linear(to-b, #000000, #000000)',
        eyebrowColor: '#FFDCC3',
        ruleColor: '#FFDCC3',
        titleColor: 'white',
      };
    case 'july_4th_sale':
      return {
        bodyColor: '#AFC6FF',
        cardGradient: 'linear(to-b, #00050e 0%, #00050e 100%)',
        eyebrowColor: '#E5A5FF',
        ruleColor: 'rgba(229, 165, 255, 0.6)',
        titleColor: 'white',
      };
    case 'july_48_48_g1':
    case 'july_48_48_g2':
      return {
        bodyColor: '#EBBAFF',
        cardGradient: 'linear(145deg, #0035B2 9%, #3A006A 82%)',
        eyebrowColor: '#AFC6FF',
        ruleColor: 'rgba(175, 198, 255, 0.6)',
        titleColor: 'white',
      };
    case 'summer_sale':
      return {
        bodyColor: 'black',
        cardGradient: 'linear(to-b, #FBFAF8 0%, #FBFAF8 100%)',
        eyebrowColor: '#EE7622',
        ruleColor: '#EE7622',
        titleColor: 'black',
      };
    case 'mba':
      return {
        bodyColor: '#FFDCA4',
        cardGradient: 'linear(to-b, #540916 0.13%, #9C0D22)',
        eyebrowColor: '#EDA93B',
        ruleColor: '#915B14',
        titleColor: 'white',
      };
    case 'marty_cagan_webinar':
      return {
        bodyColor: '#FFA6B1',
        cardGradient: 'linear(to-b, #1A0004, #540916)',
        eyebrowColor: '#FFA6B1',
        ruleColor: '#FFA6B1',
        titleColor: 'white',
      };
    case 'build_ai_agents_webinar':
      return {
        bodyColor: '#AFC6FF',
        cardGradient: 'linear(to-b, #000000, #000000)',
        eyebrowColor: '#AFC6FF',
        ruleColor: '#AFC6FF',
        titleColor: 'white',
      };
  }
}

function FreeProgramsSchoolsGrid({ imagePriority }: { imagePriority: boolean }) {
  const { height, width } = ATF_PROMO_ASSET_INTRINSIC_PX.schoolsGrid;
  return (
    <Box
      flexShrink={0}
      maxHeight={{ base: '96px', xl: '112px' }}
      maxWidth={{ base: '178px', xl: '205px' }}
      position='relative'
      width='100%'
    >
      <Image
        alt=''
        height={height}
        loading='eager'
        priority={imagePriority}
        sizes={ATF_SCHOOLS_GRID_SIZES}
        src={ATF_PROMO_ASSETS.schoolsGrid}
        width={width}
        style={{
          height: '100%',
          maxHeight: '100%',
          maxWidth: '100%',
          objectFit: 'contain',
          objectPosition: 'left center',
          width: '100%',
        }}
      />
    </Box>
  );
}

function PromoDecoration({
  imagePriority,
  promoVariant,
}: {
  imagePriority: boolean;
  promoVariant: ExperienceAtfPromoVariant;
}) {
  return (
    <Box bottom={0} left={0} pointerEvents='none' position='absolute' right={0} top={0} zIndex={1}>
      {promoVariant === 'agentic_ai' ? <AgenticAiBgDecoration imagePriority={imagePriority} /> : null}
      {promoVariant === 'july_4th_sale' || promoVariant === 'summer_sale' ? (
        <SitewideSaleAtfBgDecoration
          imagePriority={imagePriority}
          variant={promoVariant === 'july_4th_sale' ? 'july_4th' : 'summer'}
        />
      ) : null}
      {promoVariant === 'july_48_48_g1' || promoVariant === 'july_48_48_g2' ? (
        <July4848AtfBgDecoration
          imagePriority={imagePriority}
          variant={promoVariant === 'july_48_48_g1' ? 'g1' : 'g2'}
        />
      ) : null}
      {promoVariant === 'marty_cagan_webinar' ? <MartyCaganWaveBackdrop /> : null}
      {promoVariant === 'build_ai_agents_webinar' ? (
        <BuildAiAgentsWebinarBgDecoration imagePriority={imagePriority} />
      ) : null}
      {promoVariant === 'mba' ? <MbaBgDecoration imagePriority={imagePriority} /> : null}
    </Box>
  );
}

function SitewideSaleAtfBgDecoration({
  imagePriority,
  variant,
}: {
  imagePriority: boolean;
  variant: Summer2026SaleVariant;
}) {
  const config = SUMMER_2026_SALE_VARIANTS[variant];

  return (
    <Box bottom={0} left={0} position='absolute' right={0} top={0}>
      <Image
        alt=''
        loading='eager'
        priority={imagePriority}
        sizes={ATF_PROMO_DECORATION_SIZES}
        src={config.experienceAtfBg}
        style={{
          objectFit: config.experienceAtfObjectFit,
          objectPosition: config.experienceAtfObjectPosition,
        }}
        fill
      />
    </Box>
  );
}

/** 48/48 July promo ATF background — G1/G2 ship different "48" art (Figma 1672:3953 / 1672:4107). */
function July4848AtfBgDecoration({
  imagePriority,
  variant,
}: {
  imagePriority: boolean;
  variant: July4848PromoVariant;
}) {
  const config = JULY_48_48_PROMO_VARIANTS[variant];
  return (
    <Box bottom={0} left={0} position='absolute' right={0} top={0}>
      <Image
        alt=''
        loading='eager'
        priority={imagePriority}
        quality={90}
        sizes={ATF_PROMO_DECORATION_SIZES}
        src={config.experienceAtfBg}
        style={{ objectFit: 'cover', objectPosition: 'center bottom' }}
        fill
      />
    </Box>
  );
}

function MbaBgDecoration({ imagePriority }: { imagePriority: boolean }) {
  return (
    <Box bottom={0} left={0} position='absolute' right={0} top={0}>
      <Box height='100%' position='relative' width='100%'>
        <Image
          alt=''
          loading='eager'
          priority={imagePriority}
          sizes={ATF_PROMO_DECORATION_SIZES}
          src={ATF_PROMO_ASSETS.mbaBg}
          style={{ objectFit: 'cover', objectPosition: 'center -40px' }}
          fill
        />
        <Box
          bgGradient='linear(to-b, rgba(84, 9, 22, 0.5) 0%, transparent 50%)'
          bottom={0}
          left={0}
          position='absolute'
          right={0}
          top={0}
        />
      </Box>
    </Box>
  );
}

function AgenticAiBgDecoration({ imagePriority }: { imagePriority: boolean }) {
  return (
    <Box bottom={0} left={0} position='absolute' right={0} top={0}>
      <Box height='100%' position='relative' width='100%'>
        <Image
          alt=''
          loading='eager'
          priority={imagePriority}
          sizes={ATF_PROMO_DECORATION_SIZES}
          src={ATF_PROMO_ASSETS.agenticAiBg}
          style={{ objectFit: 'cover', objectPosition: 'center bottom' }}
          fill
        />
      </Box>
    </Box>
  );
}

/** Figma 7665:32891 — centered wave bg (@2x PNG), vertically flipped, behind all Marty card content. */
function MartyCaganWaveBackdrop() {
  return (
    <Box
      height='100%'
      left='50%'
      pointerEvents='none'
      position='absolute'
      top='50%'
      transform='translate(-50%, -50%)'
      width='100%'
      zIndex={1}
    >
      <Box height='100%' position='relative' width='100%'>
        <Image
          alt=''
          sizes={ATF_PROMO_DECORATION_SIZES}
          src={ATF_PROMO_ASSETS.martyCaganWaveBg}
          style={{ objectFit: 'cover', objectPosition: 'center center' }}
          fill
        />
      </Box>
    </Box>
  );
}

/** Figma 7665:32883 (desktop) / 7665:32984 (mobile) — portrait row. */
function MartyCaganPortrait({ imagePriority }: { imagePriority: boolean }) {
  return (
    <Box
      bg='#0A0B0F'
      borderRadius='6px'
      height={{ base: '100px', xl: '120px' }}
      overflow='hidden'
      position='relative'
      width={{ base: '83px', xl: '100px' }}
    >
      <Image
        alt='Marty Cagan'
        height={ATF_PROMO_ASSET_INTRINSIC_PX.martyCaganPortrait.height}
        loading='eager'
        priority={imagePriority}
        src={ATF_PROMO_ASSETS.martyCaganPortrait}
        width={ATF_PROMO_ASSET_INTRINSIC_PX.martyCaganPortrait.width}
        style={{
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center top',
          width: '100%',
        }}
      />
    </Box>
  );
}

/** Figma 888:27855 — portrait + live-session date badge. */
function BuildAiAgentsWebinarPortraitAndDateRow({ imagePriority }: { imagePriority: boolean }) {
  return (
    <Flex alignItems='flex-end' gap={{ base: '8px', xl: '16px' }} marginTop='auto'>
      <Box
        bg='black'
        border='1px solid'
        borderColor='rgba(255, 255, 255, 0.1)'
        borderRadius='6px'
        flex={1}
        height={{ base: '100px', xl: '140px' }}
        overflow='hidden'
        position='relative'
      >
        <Image
          alt='Peter Kowalchuk'
          height={ATF_PROMO_ASSET_INTRINSIC_PX.buildAiAgentsWebinarPortrait.height}
          loading='eager'
          priority={imagePriority}
          src={ATF_PROMO_ASSETS.buildAiAgentsWebinarPortrait}
          width={ATF_PROMO_ASSET_INTRINSIC_PX.buildAiAgentsWebinarPortrait.width}
          style={{
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            width: '100%',
          }}
        />
      </Box>
      <Box bg='#0A0B0F' borderRadius='4px' flexShrink={0} padding='8px' width='120px'>
        <Text
          color='#FFB27D'
          fontFamily='condensed'
          fontSize='15px'
          fontWeight='500'
          letterSpacing='0.08em'
          lineHeight={1.2}
          textTransform='uppercase'
        >
          Thursday
          <br />
          June 25, 9am PT
        </Text>
      </Box>
    </Flex>
  );
}

/** Figma 888:27863 — flipped agentic network art behind the live webinar card. */
function BuildAiAgentsWebinarBgDecoration({ imagePriority }: { imagePriority: boolean }) {
  return (
    <Box
      height='100%'
      left='50%'
      pointerEvents='none'
      position='absolute'
      top='50%'
      transform='translate(-50%, -50%) scaleY(-1)'
      width='100%'
      zIndex={1}
    >
      <Box height='100%' opacity={0.8} position='relative' width='100%'>
        <Image
          alt=''
          loading='eager'
          priority={imagePriority}
          sizes={ATF_PROMO_DECORATION_SIZES}
          src={ATF_PROMO_ASSETS.agenticAiBg}
          style={{ objectFit: 'cover', objectPosition: 'center center' }}
          fill
        />
      </Box>
    </Box>
  );
}
